package dev.lingvo.ops

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.saveToLocalStorage
import dev.lingvo.site.ds.COLOR_MODE_KEY
import dev.lingvo.site.ds.LocalEntranceOwned
import org.jetbrains.compose.web.css.vh

/**
 * The console's root, and the one place its no-motion boundary can actually be declared.
 *
 * `scripts/check-ops-motion.py` bans the tokens that animate — `LCountUp`, `LRouteTransition`,
 * `enterStaggered`, `animation(` — and walks `ops/src/ops` to do it. That catches motion this
 * module *writes* and cannot see motion it *imports*: `LAlert` and `LEmptyState` in `:kit` apply
 * their own 6px rise through `Modifier.enterRiseUnlessOwned()`, and the console draws both — the
 * failure and offline banners, the signed-out and unreachable banners, the empty attention panel,
 * the empty search result. Every one of those was playing `lingvo-enter` on mount while the guard
 * reported green, because no line under `ops/src/ops` mentions an animation at all.
 *
 * `LocalEntranceOwned` is the opt-out those components already read: it means "something above has
 * already accounted for this arrival, so do not announce it yourself". Provided `true` here it is
 * the module saying so once, for every surface it will ever mount, rather than a `noTransition()`
 * per call site that the next page to draw a banner would forget. The guard now requires this line
 * to be present, so deleting it fails `:ops:checkOpsMotion` rather than silently restoring the
 * motion.
 *
 * Colour-mode changes still cross-fade: `SmoothColorStyle` is the light/dark toggle, which is a
 * deliberate transition on a control the operator just used, not an entrance they did not ask for.
 */
@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    SilkApp {
        // Persist the operator's choice so the toggle survives a reload. Storage is per-origin, so
        // this writes the console origin's own copy even though the key is shared with the product.
        val colorMode = ColorMode.current
        LaunchedEffect(colorMode) { colorMode.saveToLocalStorage(COLOR_MODE_KEY) }
        CompositionLocalProvider(LocalEntranceOwned provides true) {
            Surface(SmoothColorStyle.toModifier().minHeight(100.vh)) { content() }
        }
    }
}
